import React, { FC, useMemo, useState } from 'react';
import {
  Button,
  CircleButton,
  ColorInput,
  EasyModal,
  FormTabSwitcher,
  Input,
  Select,
  TextArea,
  Toggle,
} from '@ui';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import useTranslation from '@app/hooks/use-translation';
import {
  MonitoringFormCriteriaCreateType,
  MonitoringFormCriteriaObjectType,
  MonitoringFormInfoType,
} from '@app/interfaces/qm-module/qm-manage/qm-monitoring-forms.type';
import { formFieldBaseStylesModal650, formFieldNameBaseStyles } from '@app/styles/base-styles';
import {
  useCreateMFCriteriaMutation,
  useEditMFCriteriaMutation,
  useGetMFBlocksBulkQuery,
} from '@app/store/api/qm-module/qm-manage/qm-manage-monitoring-forms.api';
import { useParams } from 'react-router-dom';
import { OptionItem } from '@app/components/ui/select/select.type';
import { pictogramsData } from '@app/components/metric-table-value-build';
import cn from 'classnames';

import HandleMetricSpecialToggle from '@app/components/ui/segmented-control/segmented-control';
import { MetricListArrType } from '@app/interfaces/pages-types/anatylics-metric.type';
import {
  useGetScoreTemplatesListQuery,
  useLazyGetSingleScoreTemplateQuery,
} from '@app/store/api/qm-module/qm-score-templates/qm-score-templates.api';
import { useGetGptGroupsWithMetricsQuery } from '@app/store/api/gpt-metric.api';

type FormType<T extends 'AUTO' | 'MANUAL'> = MonitoringFormCriteriaCreateType<T>;

const criteria_eval_method = [
  { title: 'Авто', value: 'AUTO' },
  { title: 'Ручная', value: 'MANUAL' },
];
const criteria_rating_type = [
  { title: 'Взвешенная оценка', value: 'WEIGHT' },
  { title: 'Простая оценка', value: 'SCORE' },
];

const styles = {
  WEIGHT: {
    AUTO: {
      type: cn(' w-full max-w-[150px]'),
      condition: cn('max-w-[180px] w-full'),
      rating: cn('max-w-[120px] w-full'),
      weight: cn('max-w-[70px] w-full ml-[20px]'),
      score_: cn('max-w-[70px] w-full'),
      description: cn('max-w-[180px] w-full '),
      color: cn('max-w-[50px] w-full'),
      icon: cn('w-full max-w-[80px] mr-[50px]'),
      default_value: cn('hidden w-0 h-0'),
    },
    MANUAL: {
      value: cn('max-w-[140px] w-full'),
      rating: cn('max-w-[140px] w-full'),
      weight: cn('max-w-[120px] w-full'),
      score_: cn('max-w-[70px] w-full'),
      description: cn('max-w-[350px] w-full grow'),
      color: cn('max-w-[50px] w-full ml-[20px] mr-[20px]'),
      icon: cn('w-full max-w-[80px]'),
      default_value: cn('w-full max-w-[100px]'),
    },
  },
  SCORE: {
    AUTO: {
      type: cn(' w-full max-w-[150px]'),
      condition: cn('max-w-[180px] w-full'),
      rating: cn('max-w-[120px] w-full'),
      weight: cn('max-w-[70px] w-full ml-[30px]'),
      description: cn('max-w-[220px] w-full ml-[30px]'),
      color: cn('max-w-[50px] w-full'),
      icon: cn('w-full max-w-[80px] mr-[50px]'),
      default_value: cn('hidden w-0 h-0'),
    },
    MANUAL: {
      value: cn('max-w-[140px] w-full'),
      rating: cn('max-w-[140px] w-full'),
      weight: cn('max-w-[120px] w-full'),
      description: cn('max-w-[350px] w-full grow'),
      color: cn('max-w-[50px] w-full ml-[60px]'),
      icon: cn('w-full max-w-[80px]'),
      default_value: cn('w-full max-w-[100px]'),
    },
  },
};

// const stylesByLabelTagAuto = {
//   type: cn(' w-full max-w-[150px]'),
//   condition: cn('max-w-[180px] w-full'),
//   rating: cn('max-w-[120px] w-full'),
//   weight: cn('max-w-[70px] w-full ml-[30px]'),
//   description: cn('max-w-[220px] w-full ml-[30px]'),
//   color: cn('max-w-[50px] w-full'),
//   icon: cn('w-full max-w-[80px] mr-[50px]'),
//   default_value: cn('hidden w-0 h-0'),
// };
// const stylesByLabelTagManual = {
//   value: cn('max-w-[140px] w-full'),
//   rating: cn('max-w-[140px] w-full'),
//   weight: cn('max-w-[120px] w-full'),
//   description: cn('max-w-[350px] w-full grow'),
//   color: cn('max-w-[50px] w-full ml-[60px]'),
//   icon: cn('w-full max-w-[80px]'),
//   default_value: cn('w-full max-w-[100px]'),
// };
const labelTags = [
  'type',
  'condition',
  'value',
  'rating',
  'score_',
  'description',
  'color',
  'icon',
  'default_value',
];

// const criteria_rating_scale_value_type = [
//   { title: 'Одно значение', value: 'single_value' },
//   { title: 'Диапазон', value: 'range' },
// ];
type CriteriaFormCreatePropsType = {
  onClose?(): void;
  editableCriteriaData: MonitoringFormCriteriaObjectType | undefined;
  metricsList: MetricListArrType[] | undefined;
  monitoringFormData: MonitoringFormInfoType;
  disable?: boolean;
};

const CriteriaFormCreate: FC<CriteriaFormCreatePropsType> = (props) => {
  const { onClose, editableCriteriaData, metricsList, monitoringFormData, disable } = props;
  const { id } = useParams();
  const { t } = useTranslation('pages.qmManage.monitoring_forms.createMonitoringForm');
  const { t: tForm } = useTranslation('common.forms');
  const { data: gptMetricList } = useGetGptGroupsWithMetricsQuery(
    {
      params: {
        project_id: monitoringFormData?.project.project_id || '',
      },
    },
    {
      skip:
        monitoringFormData?.project.project_type === 'chats' ||
        !monitoringFormData?.project.project_id,
      refetchOnMountOrArgChange: true,
    },
  );
  const gpt_metrics = useMemo(
    () => gptMetricList?.gpt_groups.flatMap((group) => group.gpt_metrics) || [],
    [gptMetricList?.gpt_groups],
  );
  const metricsOptions: OptionItem[] =
    [...(metricsList || []), ...(gpt_metrics || [])]?.map((metric) => ({
      title: metric.name,
      value: metric.metric_id,
    })) || [];
  const [criteriaType, setCriteriaType] = useState<'AUTO' | 'MANUAL'>(
    editableCriteriaData?.type || 'AUTO',
  );
  const [scoreTemplatesModal, setScoreTemplatesModalState] = useState(false);
  const [blockId, setBlockId] = useState<string | undefined>(editableCriteriaData?.cblock?.id);
  const rating_scale_type_options: OptionItem[] = [
    { value: 'eq', title: t('criteria_eq') },
    { value: '>', title: '>' },
    { value: '<', title: '<' },
    { value: '>=', title: '>=' },
    { value: '<=', title: '<=' },
    { value: '=', title: '=' },
    { value: 'range', title: t('criteria_range') },
  ];
  // api
  const [createCriteria] = useCreateMFCriteriaMutation();
  const [editCriteria] = useEditMFCriteriaMutation();
  const { data: MFormsBlocksList } = useGetMFBlocksBulkQuery({ id: id || '' }, { skip: !id });
  const { data: templatesList } = useGetScoreTemplatesListQuery();
  const [getTemplateData] = useLazyGetSingleScoreTemplateQuery();

  // api

  const {
    register,
    setValue,
    watch,
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormType<typeof criteriaType>>({
    defaultValues: editableCriteriaData
      ? { ...editableCriteriaData }
      : {
          rating_type: monitoringFormData.rating_type || 'SCORE',
          comments: { type: 'INPUT', settings: { display_type: 'VALUE' } },
          settings: {
            rating_scale: {
              items: [
                {
                  type: '>',
                  rating: 0,
                  desc: '',
                  icon: undefined,
                  color: '#ffffff',
                  id: 0,
                  value: [],
                },
              ],
            },
          },
        },
  });

  const {
    fields,
    append: appendRatingScaleItem,
    remove: removeRatingScaleItem,
  } = useFieldArray({ control, name: 'settings.rating_scale.items' });

  const {
    fields: comments,
    append: appendCommentItem,
    remove: removeCommentItem,
  } = useFieldArray({ control, name: 'comments.settings.select' });

  const [criticalBlock, setCriticalBlock] = useState(typeof watch('crit_block') === 'number');
  const [criticalMonitForm, setCriticalMonitForm] = useState(
    typeof watch('crit_mform') === 'number',
  );

  const metricValueType = [...(metricsList || []), ...(gpt_metrics || [])]?.find(
    ({ metric_id }) => metric_id === watch('settings.metric_id'),
  )?.result_value_type as 'str' | 'num' | undefined;

  function handleAppendRatingScaleNewItem() {
    appendRatingScaleItem(
      criteriaType === 'AUTO' ? defaultForAutoRatingScale : defaultForManualRatingScale,
    );
  }

  function handleRemoveRatingScaleEditItem(index: number) {
    removeRatingScaleItem(index);
  }
  function handleAppendCommentItem() {
    appendCommentItem({ id: comments.length + 1, color: '#000000', desc: '', icon: undefined });
  }

  function handleRemoveCommentItem(index: number) {
    removeCommentItem(index);
  }

  function handleOpenScoreTemplatesModal() {
    setScoreTemplatesModalState(true);
  }

  function handleCloseScoreTemplatesModal() {
    setScoreTemplatesModalState(false);
  }

  function handleSelectTemplate(rs_template_id: string) {
    getTemplateData({ rs_template_id })
      .unwrap()
      .then((data) => {
        setValue('settings.rating_scale.name', data.rating_scale.name);
        setValue('settings.rating_scale.items', []);
        for (let i = 0; i < data.rating_scale.items.length; i++) {
          const element = data.rating_scale.items[i];
          appendRatingScaleItem(element);
        }
      })
      .finally(handleCloseScoreTemplatesModal);
  }

  const visualizationsOptions = [
    { title: t('pictogram'), value: 'ICON' },
    { title: t('value'), value: 'VALUE' },
    { title: t('both'), value: 'VALUE_ICON' },
  ];
  const comTypeOptions = [
    { title: t('select_comment'), value: 'SELECT' },
    { title: t('input_comment'), value: 'INPUT' },
  ];
  const defaultForAutoRatingScale: FormType<'AUTO'>['settings']['rating_scale']['items'][0] = {
    rating: 0,
    desc: '',
    icon: undefined,
    color: '#ffffff',
    type: 'eq',
    id: fields.length + 1,
    value: [],
  };
  const defaultForManualRatingScale: FormType<'MANUAL'>['settings']['rating_scale']['items'][0] = {
    rating: 0,
    desc: '',
    icon: undefined,
    color: '#000000',
    value: '',
    id: fields.length + 1,
  };

  function handleChangeMetric(value: string | number) {
    setValue('settings.metric_id', value as string);
  }

  const monitoringFormsCriteriaBlocksOptions: OptionItem[] =
    MFormsBlocksList?.map((block) => ({ value: block.id, title: block.name })) || [];

  const iconsOptionList = Object.entries(pictogramsData).map(([iconIndex]) => {
    return { value: iconIndex, title: iconIndex };
  });

  function onSubmit(data: FormType<typeof criteriaType>) {
    if (!id) return;
    const formatedData: MonitoringFormCriteriaCreateType<typeof criteriaType> = {
      ...data,
      type: criteriaType,
      crit_block: criticalBlock ? data.crit_block : undefined,
      crit_mform: criticalMonitForm ? data.crit_mform : undefined,
      comments:
        data.comments.type === 'INPUT' ? { type: 'INPUT', settings: null } : { ...data.comments },
      settings: {
        ...data.settings,
        rating_scale: {
          ...data.settings.rating_scale,
          items: data.settings.rating_scale.items.map((item, index) => ({
            ...item,
            id: index + 1,
            value: item.value,
          })),
        },
        na_option: criteriaType === 'MANUAL' ? !!watch('settings.na_option') : undefined,
      },
    };
    if (editableCriteriaData) {
      editCriteria({
        params: { mform_id: id, cblock_id: blockId, id: editableCriteriaData.id },
        body: { ...formatedData },
      })
        .unwrap()
        .then(() => onClose?.());
      return;
    }
    createCriteria({ params: { mform_id: id, cblock_id: blockId }, body: formatedData })
      .unwrap()
      .then(() => onClose?.());
  }

  return (
    <>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="w-full flex flex-col gap-[25px] relative pt-[20px]"
      >
        <div className={formFieldBaseStylesModal650}>
          <LabelMiniJsx label={t('title')} />
          <Input
            {...register('name', { required: { value: true, message: tForm('required') } })}
            error={errors.name?.message}
          />
        </div>
        <div className={formFieldBaseStylesModal650}>
          <LabelMiniJsx label={t('block')} />
          <Select
            withSearch
            options={[
              {
                title: 'Без блока',
                value: undefined as unknown as string,
              },
              ...monitoringFormsCriteriaBlocksOptions,
            ]}
            defaultValue={blockId}
            onChange={(value) => setBlockId(value as string)}
          />
        </div>
        <div className={formFieldBaseStylesModal650}>
          <LabelMiniJsx label={t('description')} />
          <Controller
            render={() => (
              <TextArea
                defaultValue={watch('description')}
                height={100}
                onChange={(val) => setValue('description', val.target.value)}
                error={errors.description?.message}
              />
            )}
            rules={{ validate: (val) => !!val || tForm('required') }}
            name={'description'}
            control={control}
          />
        </div>
        <div className={formFieldBaseStylesModal650}>
          <LabelMiniJsx label={t('criteria_eval_method')} />
          <FormTabSwitcher
            valuesList={criteria_eval_method}
            isActive={criteriaType}
            onChange={(value: FormType<typeof criteriaType>['type']) => {
              reset({
                ...watch(),
                settings: {
                  ...watch().settings,
                  na_option: undefined,
                  metric_id: undefined,
                  rating_scale: {
                    ...watch().settings.rating_scale,
                    name: undefined,
                    items: [
                      value === 'AUTO' ? defaultForAutoRatingScale : defaultForManualRatingScale,
                    ],
                  },
                },
              });
              setCriteriaType(value);
            }}
          />
        </div>
        {criteriaType === 'AUTO' && (
          <div className={formFieldBaseStylesModal650}>
            <LabelMiniJsx label={t('criteria_metric')} />
            {}
            <Controller
              render={() => (
                <Select
                  withSearch
                  options={metricsOptions}
                  onChange={handleChangeMetric}
                  defaultValue={watch('settings.metric_id')}
                  error={errors?.settings?.['metric_id']?.message}
                />
              )}
              name={'settings.metric_id'}
              rules={{
                validate: (value) =>
                  criteriaType === 'AUTO' ? !!value || tForm('required') : true,
              }}
              control={control}
            />
          </div>
        )}
        <div className={formFieldBaseStylesModal650}>
          <LabelMiniJsx label={t('criteria_rating_type')} />
          <FormTabSwitcher
            valuesList={criteria_rating_type}
            isActive={watch('rating_type')}
            disabled={!!monitoringFormData.rating_type}
            onChange={(value: FormType<typeof criteriaType>['rating_type']) => {
              setValue('rating_type', value);
            }}
          />
        </div>
        <div className={formFieldBaseStylesModal650}>
          <LabelMiniJsx label={t('scale_name')} />
          <Input
            {...register('settings.rating_scale.name', {
              required: { value: true, message: tForm('required') },
            })}
            error={errors?.settings?.['rating_scale']?.['name']?.message}
          />
        </div>

        {/*"RATING SCALE ITEMS"*/}
        <div className="flex flex-col gap-[20px]">
          {criteriaType === 'AUTO' && (
            <div className="w-full flex items-center justify-between">
              <LabelMiniJsx label={t('rating_scale')} />
              <Button
                onClick={handleOpenScoreTemplatesModal}
                label={t('fill_from_template')}
                fill="linked"
              />
            </div>
          )}

          {!!fields.length && (
            <div className="flex items-center w-[calc(100%)] justify-between">
              {(criteriaType === 'AUTO'
                ? labelTags
                    .filter((label) => label !== 'value')
                    .filter((label) => {
                      if (watch('rating_type') === 'SCORE') {
                        return label !== 'score_';
                      } else return true;
                    })
                    .map((i) => {
                      if (i === 'rating' && watch('rating_type') === 'WEIGHT') {
                        return 'weight';
                      }
                      return i;
                    })
                : labelTags
                    .filter((label) => {
                      if (watch('rating_type') === 'SCORE') {
                        return label !== 'score_';
                      } else return true;
                    })
                    .slice(2)
                    .map((i) => {
                      if (i === 'rating' && watch('rating_type') === 'WEIGHT') {
                        return 'weight';
                      }
                      return i;
                    })
              ).map((label) => (
                <div
                  key={label}
                  className={cn(
                    styles?.[watch('rating_type')]?.[criteriaType]?.[label],
                    'text-center',
                  )}
                >
                  <LabelMiniJsx label={t(`rating_scale_${label}`)} />
                </div>
              ))}
            </div>
          )}
          {fields.map((field, index) => (
            <div key={field.id} className="flex items-center gap-[20px]">
              {criteriaType === 'AUTO' && (
                <div className="w-full max-w-[140px]">
                  <Select
                    options={
                      watch('settings.metric_id')
                        ? metricValueType === 'str'
                          ? rating_scale_type_options.slice(0, 1)
                          : rating_scale_type_options.slice(1)
                        : rating_scale_type_options
                    }
                    defaultValue={watch(`settings.rating_scale.items.${index}.type`)}
                    onChange={(value) => {
                      setValue(
                        `settings.rating_scale.items.${index}.type`,
                        value as FormType<
                          typeof criteriaType
                        >['settings']['rating_scale']['items'][0]['type'],
                      );
                    }}
                  />
                </div>
              )}
              {criteriaType === 'AUTO' && (
                <div className="w-full flex items-center gap-[10px] max-w-[200px]">
                  <Input
                    {...register(
                      `settings.rating_scale.items.${index}.value${
                        watch(`settings.rating_scale.items.${index}.type`) === 'range' ? '.0' : ''
                      }`,
                    )}
                    type={metricValueType && metricValueType === 'num' ? 'number' : 'text'}
                  />
                  {watch(`settings.rating_scale.items.${index}.type`) === 'range' && (
                    <Input
                      {...register(`settings.rating_scale.items.${index}.value.1`)}
                      type={metricValueType && metricValueType === 'num' ? 'number' : 'text'}
                    />
                  )}
                </div>
              )}
              {criteriaType === 'MANUAL' && (
                <div className="w-full max-w-[140px]">
                  <Input
                    {...register(`settings.rating_scale.items.${index}.value`)}
                    placeholder={t('rating_scale_value')}
                  />
                </div>
              )}
              <div className="w-full max-w-[100px]">
                <Input
                  {...register(`settings.rating_scale.items.${index}.rating`, {
                    // valueAsNumber: true,
                    max: { value: 100, message: '<=100%' },
                  })}
                  placeholder={t('rating_scale_rating')}
                  step={0.1}
                  type="number"
                  error={errors.settings?.rating_scale?.items?.[index]?.rating?.message}
                />
              </div>
              {watch('rating_type') === 'WEIGHT' && (
                <div className="min-w-[34px] max-w-[34px]">
                  {monitoringFormData.evaluation?.score?.max &&
                  typeof watch(`settings.rating_scale.items.${index}.rating`) === 'number'
                    ? (
                        (watch(`settings.rating_scale.items.${index}.rating`) / 100) *
                        (monitoringFormData.evaluation.score.max -
                          (monitoringFormData.evaluation.score?.min || 0))
                      ).toFixed(2)
                    : null}
                </div>
              )}
              {/* <div className="w-full"> */}
              <Input
                {...register(`settings.rating_scale.items.${index}.desc`, {
                  required: { value: true, message: tForm('required') },
                })}
                placeholder={t('rating_scale_description')}
                error={errors?.settings?.['rating_scale']?.['items']?.[index]?.['desc']?.message}
              />
              {/* </div> */}
              <ColorInput
                color={watch(`settings.rating_scale.items.${index}.color`)}
                changeColor={(color) =>
                  setValue(`settings.rating_scale.items.${index}.color`, color)
                }
              />
              <div className="w-full max-w-[100px]">
                <Select
                  defaultValue={
                    String(watch(`settings.rating_scale.items.${index}.icon`)) || undefined
                  }
                  onChange={(value) =>
                    setValue(`settings.rating_scale.items.${index}.icon`, Number(value))
                  }
                  dropTop
                  options={iconsOptionList}
                  // error={errors.settings?.select?.[index]?.icon?.message}
                  pictogramsMode
                  position="right"
                  pictogramListSize={28}
                  dropWidth={320}
                />
              </div>
              {criteriaType === 'MANUAL' && (
                <div>
                  {/* <LabelMiniJsx label={t('default_value')} /> */}
                  {/* <Select
                  options={fields.map((field, index) => ({ value: index, title: field.desc }))}
                  onChange={(val) => setValue('settings.rating_scale.default', Number(val))}
                  defaultValue={watch('settings.rating_scale.default')}
                  customClassName="hidden h-0 w-0"
                /> */}
                  <Toggle
                    onChecked={() => setValue('settings.rating_scale.default', index + 1)}
                    checked={watch('settings.rating_scale.default') === index + 1}
                  />
                </div>
              )}
              <div>
                <CircleButton
                  icon={'TrashIcon'}
                  size={14}
                  className="text-basic_red"
                  onClick={() => handleRemoveRatingScaleEditItem(index)}
                />
              </div>
            </div>
          ))}
          <div className="flex w-full items-center justify-end">
            <CircleButton
              icon={'PlusIcon'}
              className="text-action"
              size={14}
              onClick={handleAppendRatingScaleNewItem}
            />
          </div>
        </div>
        {/*"RATING SCALE ITEMS"*/}
        {/* {criteriaType === 'MANUAL' && (
        <div className="flex items-center justify-between">
          <LabelMiniJsx label={t('na_value')} />
          <Toggle
            checked={watch('settings.na_option')}
            onChecked={(val) => setValue('settings.na_option', val)}
          />
        </div>
      )} */}
        {/**/}
        <div className="flex items-baseline justify-between">
          <LabelMiniJsx label={t('critical_block')} />
          <div className="flex flex-col gap-[10px] w-[200px] ">
            <div className="self-end">
              <Toggle checked={criticalBlock} onChecked={(val) => setCriticalBlock(val)} />
            </div>
            {criticalBlock && <Input {...register('crit_block', { valueAsNumber: true })} />}
          </div>
        </div>
        <div className="flex items-baseline justify-between">
          <LabelMiniJsx label={t('critical_monit_form')} />
          <div className="flex flex-col gap-[10px] w-[200px] ">
            <div className="self-end">
              <Toggle checked={criticalMonitForm} onChecked={(val) => setCriticalMonitForm(val)} />
            </div>
            {criticalMonitForm && <Input {...register('crit_mform', { valueAsNumber: true })} />}
          </div>
        </div>
        <div className={formFieldBaseStylesModal650}>
          <LabelMiniJsx label={t('comments_type')} />
          <div>
            <HandleMetricSpecialToggle
              isActive={watch('comments.type')}
              onChange={(value) => {
                setValue('comments.type', value as 'SELECT' | 'INPUT');
              }}
              valuesList={comTypeOptions}
            />
          </div>
        </div>
        {watch('comments.type') === 'SELECT' && (
          <div className={formFieldBaseStylesModal650}>
            <label className={formFieldNameBaseStyles}>{t('display_type')}</label>
            <div>
              <HandleMetricSpecialToggle
                isActive={watch('comments.settings.display_type')}
                onChange={(value) => {
                  setValue(
                    'comments.settings.display_type',
                    value as 'VALUE' | 'ICON' | 'VALUE_ICON',
                  );
                }}
                valuesList={visualizationsOptions}
              />
            </div>
          </div>
        )}
        {/*COMMENTS ITEMS*/}
        {watch('comments.type') === 'SELECT' && (
          <div className="flex flex-col gap-[20px]">
            <LabelMiniJsx label={t('comments_select_items_title')} />
            <div className=" flex w-full flex-col items-end gap-[10px] justify-end">
              {comments.map((field, index) => (
                <div key={field.id} className="flex w-[50%] items-center gap-[20px]">
                  <Input
                    {...register(`comments.settings.select.${index}.desc`, {
                      required: {
                        value:
                          watch('comments.settings.display_type') === 'VALUE' ||
                          watch('comments.settings.display_type') === 'VALUE_ICON',
                        message: ' ',
                      },
                    })}
                    error={errors.comments?.settings?.select?.[index]?.desc?.message}
                    placeholder={t('rating_scale_description')}
                  />
                  <ColorInput
                    color={watch(`comments.settings.select.${index}.color`)}
                    changeColor={(color) =>
                      setValue(`comments.settings.select.${index}.color`, color)
                    }
                  />
                  <div className="max-w-[90px] w-full">
                    <Controller
                      render={() => (
                        <Select
                          defaultValue={
                            String(watch(`comments.settings.select.${index}.icon`)) || undefined
                          }
                          onChange={(value) =>
                            setValue(`comments.settings.select.${index}.icon`, Number(value))
                          }
                          dropTop
                          options={iconsOptionList}
                          // error={errors.settings?.select?.[index]?.icon?.message}
                          pictogramsMode
                          position="right"
                          pictogramListSize={28}
                          dropWidth={320}
                          error={errors.comments?.settings?.select?.[index]?.icon?.message}
                        />
                      )}
                      rules={{
                        required: {
                          value:
                            watch('comments.settings.display_type') === 'ICON' ||
                            watch('comments.settings.display_type') === 'VALUE_ICON',
                          message: ' ',
                        },
                      }}
                      control={control}
                      name={`comments.settings.select.${index}.icon`}
                    />
                  </div>
                  <div>
                    <CircleButton
                      icon={'TrashIcon'}
                      size={14}
                      className="text-basic_red"
                      onClick={() => handleRemoveCommentItem(index)}
                    />
                  </div>
                </div>
              ))}
            </div>
            <div className="flex w-full items-center justify-end">
              <CircleButton
                icon={'PlusIcon'}
                className="text-action"
                size={14}
                onClick={handleAppendCommentItem}
              />
            </div>
          </div>
        )}
        {/*COMMENTS ITEMS*/}
        {/**/}
        {/*{conditionalFormContinue[watch('eval_method')]}*/}
        {/*BUTTON BLOCK*/}
        <div className="sticky bg-white bottom-0 py-[20px] flex items-center gap-[10px]">
          <Button disabled={disable} type="submit" label={t('approve_button')} />
          <Button fill="linked" onClick={onClose} label={t('cancel_button')} />
        </div>
        {/*BUTTON BLOCK*/}
      </form>
      <EasyModal
        withoutFooter
        variant="mediumW650"
        onClose={handleCloseScoreTemplatesModal}
        show={scoreTemplatesModal}
      >
        <div className="p-2 flex flex-col gap-[10px]">
          {templatesList?.map((template, index) => (
            <div
              className="p-[12px_18px]  justify-center rounded-defaultR  border-solid border-[.5px] cursor-pointer hover:bg-bg_3/[.5] border-3color"
              key={index}
              onClick={() => handleSelectTemplate(template.rs_template_id)}
            >
              {template.name}
            </div>
          ))}
        </div>
      </EasyModal>
    </>
  );
};
const LabelMiniJsx = (props: { label: string }) => {
  return <span className={formFieldNameBaseStyles}>{props.label}</span>;
};
export default CriteriaFormCreate;
